import type { IBaseAttributes, TConstraints } from '@feathr/rachis';

import { addressConstraints } from '../address';
import type { IEmailCampaignMailing, ITriggerableEmailCampaignMailing } from './types';

/*
 * The Mailrail campaign chain and the standalone MailrailDripStep model both
 * carry the triggerable-email field constraints. Both reach IBaseAttributes
 * (which has a string index signature), so intersecting with it lets these
 * shared fragments use dot-path keys like `contact_info.premise1`.
 */
type TEmailCampaignMailingConstraints = TConstraints<IEmailCampaignMailing & IBaseAttributes>;
type TTriggerableEmailCampaignMailingConstraints = TConstraints<
  ITriggerableEmailCampaignMailing & IBaseAttributes
>;

/**
 * The `contact_info.*` constraints, derived from the shared addressConstraints.
 * Exported so consumers that toggle these exact keys (e.g. MailrailPartnerMessage,
 * which nullifies them) reference a single source.
 */
export const contactInfoConstraints: TEmailCampaignMailingConstraints = Object.fromEntries(
  Object.entries(addressConstraints).map(([key, constraint]) => [
    `contact_info.${key}`,
    constraint,
  ]),
);

/**
 * Frontend analog of the backend `EmailCampaignMailing` mixin's validation: the
 * fields every Mailrail mailing shares — template, sender, and compliance address.
 */
export const mailingConstraints: TEmailCampaignMailingConstraints = {
  template_id: {
    presence: { allowEmpty: false, message: '^Choose a template' },
  },
  from_address: {
    presence: { allowEmpty: false, message: '^A from email address must be provided' },
    email: { message: '^From email address is not valid' },
  },
  from_name: {
    presence: { allowEmpty: false, message: "^From Name can't be blank." },
    latinCharacters: true,
  },
  ...contactInfoConstraints,
};

/**
 * Frontend analog of the backend `TriggerableEmailCampaignMailing` mixin's
 * validation: the base mailing constraints plus the trigger→send delay.
 */
export const triggerableMailingConstraints: TTriggerableEmailCampaignMailingConstraints = {
  ...mailingConstraints,
  delay_seconds: {
    presence: { allowEmpty: false },
    numericality: {
      onlyInteger: true,
      greaterThanOrEqualTo: 0,
      message: '^Delay period must be a non-negative number',
    },
  },
};
